import { useQueryClient } from '@tanstack/react-query';
import {
  type CreatePackageBatchRequest,
  type CreatePackageVariantRequest,
  type PackageBatchOut,
  type PackageDetail,
} from '@utazzo/shared';
import { UzButton } from '@utazzo/ui';
import { CalendarRange, ChevronDown, Layers, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { createPackageVariant, deletePackageVariant } from '../packages.api';
import { useAddPackageBatch, useDeletePackageBatch, useUpdatePackageBatch } from '../use-packages';

import { type BatchFormSubmit, PackageBatchFormPanel } from './PackageBatchFormPanel';

export type PackageBatchDraft = {
  clientKey: string;
  batch: CreatePackageBatchRequest;
  variants: CreatePackageVariantRequest[];
};

interface BatchesVariantsEditorProps {
  pkg?: PackageDetail | null;
  draftValue?: PackageBatchDraft[];
  onDraftChange?: (items: PackageBatchDraft[]) => void;
  numberOfDays: number;
  hasItinerary: boolean;
}

const newClientKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const draftToRow = (item: PackageBatchDraft, i: number): PackageBatchOut => ({
  id: item.clientKey,
  name: item.batch.name,
  departureDate: item.batch.departureDate,
  returnDate: item.batch.returnDate ?? null,
  capacity: item.batch.capacity ?? null,
  status: item.batch.status ?? 'OPEN',
  sortOrder: item.batch.sortOrder ?? i,
  variants: item.variants.map((v, vi) => ({
    id: `${item.clientKey}-v-${vi}`,
    name: v.name,
    price: v.price,
    strikePrice: v.strikePrice ?? null,
    description: v.description ?? null,
    sortOrder: v.sortOrder ?? vi,
  })),
});

const formatDate = (iso: string): string => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const formatRupees = (value: string): string => {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  });
};

const syncBatchVariants = async (
  packageId: string,
  batchId: string,
  existing: PackageBatchOut['variants'],
  variants: CreatePackageVariantRequest[],
): Promise<void> => {
  for (const v of existing) {
    await deletePackageVariant(packageId, batchId, v.id);
  }
  for (let i = 0; i < variants.length; i++) {
    const variant = variants[i];
    if (!variant) continue;
    await createPackageVariant(packageId, batchId, { ...variant, sortOrder: i });
  }
};

export const BatchesVariantsEditor = ({
  pkg,
  draftValue,
  onDraftChange,
  numberOfDays,
  hasItinerary,
}: BatchesVariantsEditorProps): React.JSX.Element => {
  const controlled = onDraftChange !== undefined;
  const draftItems = draftValue ?? [];
  const qc = useQueryClient();

  const addBatch = useAddPackageBatch(pkg?.id ?? '');
  const updateBatch = useUpdatePackageBatch(pkg?.id ?? '');
  const delBatch = useDeletePackageBatch(pkg?.id ?? '');

  const [batchDrawer, setBatchDrawer] = useState<{
    mode: 'create' | 'edit';
    item?: PackageBatchOut;
    draftIndex?: number;
  } | null>(null);

  const rows: PackageBatchOut[] = controlled ? draftItems.map(draftToRow) : (pkg?.batches ?? []);

  const batchPending = controlled
    ? false
    : addBatch.isPending || updateBatch.isPending || delBatch.isPending;

  const submitBatch = async (payload: BatchFormSubmit): Promise<void> => {
    if (controlled) {
      const next =
        batchDrawer?.mode === 'edit' && batchDrawer.draftIndex !== undefined
          ? draftItems.map((d, i) =>
              i === batchDrawer.draftIndex
                ? {
                    ...d,
                    batch: { ...payload.batch, sortOrder: i },
                    variants: payload.variants.map((v, vi) => ({ ...v, sortOrder: vi })),
                  }
                : d,
            )
          : [
              ...draftItems,
              {
                clientKey: newClientKey(),
                batch: { ...payload.batch, sortOrder: draftItems.length },
                variants: payload.variants.map((v, vi) => ({ ...v, sortOrder: vi })),
              },
            ];
      onDraftChange?.(next);
      setBatchDrawer(null);
      return;
    }

    if (!pkg?.id) return;

    try {
      if (batchDrawer?.mode === 'edit' && batchDrawer.item) {
        await updateBatch.mutateAsync({ childId: batchDrawer.item.id, payload: payload.batch });
        await syncBatchVariants(
          pkg.id,
          batchDrawer.item.id,
          batchDrawer.item.variants,
          payload.variants,
        );
        await qc.invalidateQueries({ queryKey: ['packages', 'detail', pkg.id] });
      } else {
        const created = await addBatch.mutateAsync(payload.batch);
        for (let i = 0; i < payload.variants.length; i++) {
          const variant = payload.variants[i];
          if (!variant) continue;
          await createPackageVariant(pkg.id, created.id, { ...variant, sortOrder: i });
        }
        await qc.invalidateQueries({ queryKey: ['packages', 'detail', pkg.id] });
      }
      setBatchDrawer(null);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save batch';
      toast.error(message);
    }
  };

  const removeBatch = (row: PackageBatchOut, i: number): void => {
    if (!window.confirm('Remove this batch and all its traveller options?')) return;
    if (controlled) {
      onDraftChange?.(draftItems.filter((_, idx) => idx !== i));
      return;
    }
    delBatch.mutate(row.id);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">Batches</h3>
          <p className="text-xs text-zinc-500">
            Fixed departures with traveller pricing (e.g. Tempo Traveller, SUV).
            {hasItinerary
              ? ` Return date is calculated from your ${numberOfDays}-day itinerary.`
              : ' Add itinerary days to auto-calculate return dates.'}
          </p>
        </div>
        <UzButton
          size="sm"
          onClick={() => setBatchDrawer({ mode: 'create' })}
          disabled={batchPending}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          Add batch
        </UzButton>
      </div>

      {batchDrawer?.mode === 'create' && (
        <PackageBatchFormPanel
          mode="create"
          pending={batchPending}
          numberOfDays={numberOfDays}
          hasItinerary={hasItinerary}
          onClose={() => setBatchDrawer(null)}
          onSubmit={controlled ? undefined : submitBatch}
          onSubmitDraft={controlled ? submitBatch : undefined}
        />
      )}

      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-6 text-center text-xs text-zinc-500">
          No batches yet. Click <strong>Add batch</strong>, fill departure details and traveller
          pricing, then click <strong>Add batch</strong> in the form - batches are saved separately
          from the main package Save button and appear on the website sidebar.
        </p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row, batchIndex) => {
            const isBatchOpen =
              batchDrawer?.mode === 'edit' &&
              (controlled
                ? batchDrawer.draftIndex === batchIndex
                : batchDrawer.item?.id === row.id);

            return (
              <li key={row.id} className="rounded-lg border border-zinc-200 bg-white">
                <div
                  role="button"
                  tabIndex={0}
                  className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-zinc-50"
                  onClick={() =>
                    setBatchDrawer(
                      isBatchOpen ? null : { mode: 'edit', item: row, draftIndex: batchIndex },
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setBatchDrawer(
                        isBatchOpen ? null : { mode: 'edit', item: row, draftIndex: batchIndex },
                      );
                    }
                  }}
                >
                  <CalendarRange className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-zinc-900">{row.name}</p>
                    <p className="text-xs text-zinc-500">
                      Departs {formatDate(row.departureDate)}
                      {row.returnDate ? ` · Returns ${formatDate(row.returnDate)}` : ''}
                      {row.capacity ? ` · ${row.capacity} seats` : ''}
                      {' · '}
                      {row.status.charAt(0) + row.status.slice(1).toLowerCase()}
                    </p>
                  </div>
                  <span className="text-xs text-zinc-500">
                    {row.variants.length} option{row.variants.length === 1 ? '' : 's'}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 text-zinc-400 transition-transform ${isBatchOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                  <button
                    type="button"
                    className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-red-600"
                    aria-label="Remove batch"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeBatch(row, batchIndex);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>

                {isBatchOpen && (
                  <div className="border-t border-zinc-100 px-4 py-4">
                    <PackageBatchFormPanel
                      mode="edit"
                      item={row}
                      pending={batchPending}
                      numberOfDays={numberOfDays}
                      hasItinerary={hasItinerary}
                      onClose={() => setBatchDrawer(null)}
                      onSubmit={controlled ? undefined : submitBatch}
                      onSubmitDraft={controlled ? submitBatch : undefined}
                    />
                  </div>
                )}

                {row.variants.length > 0 && (
                  <ul className="space-y-1.5 border-t border-zinc-100 px-4 py-3">
                    {row.variants.map((variant) => (
                      <li
                        key={variant.id}
                        className="flex items-center gap-2 rounded-md border border-zinc-200 px-3 py-2"
                      >
                        <Layers className="h-3.5 w-3.5 shrink-0 text-zinc-400" aria-hidden />
                        <span className="min-w-0 flex-1 truncate text-sm text-zinc-800">
                          {variant.name}
                        </span>
                        {variant.strikePrice &&
                        Number(variant.strikePrice) > Number(variant.price) ? (
                          <span className="text-xs text-zinc-500">
                            <span className="line-through">
                              {formatRupees(variant.strikePrice)}
                            </span>
                            {' → '}
                            <span className="font-medium text-zinc-700">
                              {formatRupees(variant.price)}
                            </span>
                          </span>
                        ) : (
                          <span className="text-xs font-medium text-zinc-700">
                            {formatRupees(variant.price)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
